import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, unref as _unref, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createBlock as _createBlock, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { key: 0 }
const _hoisted_2 = {
  key: 0,
  class: "space-y-3 px-2 pb-4"
}
const _hoisted_3 = { class: "text-[clamp(1.8rem,2.2vw,2.45rem)] font-extrabold leading-tight text-[#182033]" }
const _hoisted_4 = { class: "flex flex-row justify-between" }
const _hoisted_5 = { class: "max-w-[55rem] text-[0.98rem] leading-7 text-[#6e7b92]" }
const _hoisted_6 = { class: "flex flex-wrap items-center justify-end gap-2 max-[860px]:justify-start" }
const _hoisted_7 = {
  key: 0,
  class: "flex items-center justify-between gap-4 rounded-[1.25rem] border border-[rgba(219,227,239,0.9)] bg-white/94 p-4 shadow-[0_18px_45px_rgba(15,23,42,0.05)] max-[1100px]:flex-col max-[1100px]:items-stretch"
}
const _hoisted_8 = { class: "grid w-full gap-4 md:grid-cols-2 xl:grid-cols-6" }
const _hoisted_9 = { class: "flex flex-col gap-[0.42rem] xl:col-span-2" }
const _hoisted_10 = { class: "flex flex-col gap-[0.42rem]" }
const _hoisted_11 = {
  key: 0,
  class: "flex flex-col gap-[0.42rem]"
}
const _hoisted_12 = { class: "flex flex-col gap-[0.42rem]" }
const _hoisted_13 = { class: "flex flex-col gap-[0.42rem]" }
const _hoisted_14 = { class: "border-b border-[rgba(227,233,243,0.95)] px-6 py-5" }
const _hoisted_15 = { class: "space-y-4 px-6 py-5" }
const _hoisted_16 = { class: "relative" }
const _hoisted_17 = { class: "max-h-[18rem] space-y-2 overflow-y-auto pr-1" }
const _hoisted_18 = ["onClick"]
const _hoisted_19 = { class: "min-w-0 space-y-1" }
const _hoisted_20 = { class: "truncate text-[0.95rem] font-extrabold text-[#182033]" }
const _hoisted_21 = { class: "text-[0.84rem] text-[#6e7b92]" }
const _hoisted_22 = {
  key: 0,
  class: "text-[0.84rem] text-[#6e7b92]"
}
const _hoisted_23 = { class: "font-semibold text-[#1b2436]" }
const _hoisted_24 = {
  key: 0,
  class: "rounded-[1rem] border border-dashed border-[rgba(216,228,240,0.95)] bg-[#fbfcff] px-4 py-5 text-center text-[0.92rem] text-[#6e7b92]"
}

import { computed, ref, watch, inject } from "vue";
import {
  ChevronDown,
  Download,
  Loader2,
  RefreshCcw,
  Search,
  Calendar,
  Gem,
} from "lucide-vue-next";
import { useToast } from "vue-toast-notification";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import AnimatedBox from "@/components/dashboard/AnimatedBox.vue";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type {
  DashboardClientOption,
  DashboardFilters,
  DashboardReportDownloadAction,
  DashboardReportDownloadPayload,
  DashboardRole,
} from "@/types/dashboard";


export default /*@__PURE__*/_defineComponent({
  __name: 'DashboardHeader',
  props: {
    title: {},
    subtitle: {},
    role: {},
    filters: {},
    clients: {},
    serviceOptions: {},
    packageOptions: {},
    lastUpdated: {},
    showPackageSelector: { type: Boolean },
    dateFromMin: {},
    downloadLoading: { type: Boolean },
    reportPeriod: {},
    nextReportUpdate: {},
    packageName: {}
  },
  emits: ["update:filters", "download-report"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

// comfortMode removed

const reportMenuRef = ref<HTMLElement | null>(null);
const toast = useToast();
const specificAccountDialogOpen = ref(false);
const specificAccountSearch = ref("");
const selectedSpecificClientId = ref("");

const isClientUser = computed(() => props.role === "client");
const isSuperAdmin = computed(() => props.role === "super_admin");
const isAdminOrMember = computed(() =>
  ["admin", "member"].includes(props.role)
);
const showClientSelector = computed(() => !isClientUser.value);
const showPackageSelector = computed(() => props.showPackageSelector ?? true);
const showReportMenu = computed(
  () => isSuperAdmin.value || isAdminOrMember.value
);
const isReportActionBusy = computed(() => Boolean(props.downloadLoading));

const reportActions = computed(() => {
  if (isSuperAdmin.value) {
    return [
      { value: "all", label: "All Accounts", hint: "ALL" },
      { value: "specific", label: "Specific Account", hint: "SPECIFIC" },
    ];
  }

  if (isAdminOrMember.value) {
    return [
      {
        value: "assigned",
        label: "All Assigned Accounts",
        hint: "ASSIGNED",
      },
      { value: "specific", label: "Specific Account", hint: "SPECIFIC" },
    ];
  }

  return [];
});

const filteredSpecificClients = computed(() => {
  const term = specificAccountSearch.value.trim().toLowerCase();
  return props.clients
    .filter((client) => {
      if (!term) return true;
      return [client.company_name, client.client_id, client.package_name]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term));
    })
    .slice()
    .sort((a, b) => a.company_name.localeCompare(b.company_name));
});

const selectedSpecificClient = computed(() =>
  props.clients.find(
    (client) => client.client_id === selectedSpecificClientId.value
  )
);

const formattedLastUpdated = computed(() => {
  if (!props.lastUpdated) return "Not yet refreshed";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(props.lastUpdated));
});

function updateFilter<K extends keyof DashboardFilters>(
  key: K,
  value: DashboardFilters[K]
) {
  emit("update:filters", {
    ...props.filters,
    [key]: value,
  });
}

function onClientUpdate(value: string | number) {
  updateFilter("client_id", value ? String(value) : "all");
}

function onServiceUpdate(value: string | number) {
  updateFilter(
    "service_id",
    value && value !== "__all_services" ? Number(value) : null
  );
}

function onPackageUpdate(value: string | number) {
  updateFilter(
    "package_id",
    value && value !== "__all_packages" ? Number(value) : null
  );
}

function onDateFromUpdate(value: string | number) {
  const nextValue = value ? String(value) : null;
  if (!nextValue || !props.dateFromMin) {
    updateFilter("date_from", nextValue);
    return;
  }

  updateFilter(
    "date_from",
    nextValue < props.dateFromMin ? props.dateFromMin : nextValue
  );
}

function onDateToUpdate(value: string | number) {
  const nextValue = value ? String(value) : null;
  if (!nextValue || !props.dateFromMin) {
    updateFilter("date_to", nextValue);
    return;
  }

  updateFilter(
    "date_to",
    nextValue < props.dateFromMin ? props.dateFromMin : nextValue
  );
}

function emitDownloadReport(
  action: DashboardReportDownloadAction,
  clientId: string | null = null
) {
  reportMenuRef.value?.removeAttribute("open");

  emit("download-report", {
    action,
    client_id: clientId,
    role: props.role,
    filters: {
      ...props.filters,
      client_id:
        action === "specific" && clientId ? clientId : props.filters.client_id,
    },
  });
}

function handleDownloadReportClick() {
  if (isReportActionBusy.value) return;

  if (isClientUser.value) {
    emitDownloadReport("current", null);
    return;
  }

  toast.error("Unable to open report. Please try again.");
}

function openSpecificAccountSelector() {
  if (!filteredSpecificClients.value.length) {
    toast.error("No available accounts found for report generation.");
    return;
  }

  specificAccountSearch.value = "";
  selectedSpecificClientId.value = "";
  specificAccountDialogOpen.value = true;
}

function closeSpecificAccountDialog() {
  specificAccountDialogOpen.value = false;
}

function confirmSpecificAccountReport() {
  if (!selectedSpecificClient.value) {
    toast.error("Please select a client account first.");
    return;
  }

  specificAccountDialogOpen.value = false;
  emitDownloadReport("specific", selectedSpecificClient.value.client_id);
}

function handleReportMenuAction(value: DashboardReportDownloadAction) {
  if (isReportActionBusy.value) return;

  if (value === "all" && isSuperAdmin.value) {
    emitDownloadReport("all", null);
    return;
  }

  if (value === "assigned" && isAdminOrMember.value) {
    emitDownloadReport("assigned", null);
    return;
  }

  if (value === "specific" && (isSuperAdmin.value || isAdminOrMember.value)) {
    openSpecificAccountSelector();
    return;
  }

  if (value === "current" && isClientUser.value) {
    emitDownloadReport("current", null);
    return;
  }

  toast.error("Unable to open report. Please try again.");
}

watch(specificAccountDialogOpen, (open) => {
  if (!open) {
    specificAccountSearch.value = "";
    selectedSpecificClientId.value = "";
  }
});

return (_ctx: any,_cache: any) => {
  return (__props.role === 'super_admin')
    ? (_openBlock(), _createElementBlock("section", _hoisted_1, [
        _createVNode(AnimatedBox, { orientation: "y" }, {
          default: _withCtx(() => [
            _createElementVNode("div", null, [
              (__props.role === 'super_admin')
                ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                    _createElementVNode("h1", _hoisted_3, _toDisplayString(__props.title), 1),
                    _createElementVNode("div", _hoisted_4, [
                      _createElementVNode("p", _hoisted_5, _toDisplayString(__props.subtitle), 1),
                      _createElementVNode("div", _hoisted_6, [
                        _createVNode(_unref(Badge), {
                          variant: "outline",
                          class: "rounded-full border-[rgba(217,226,238,0.95)] bg-[#edf4ff] px-3 py-1 text-[0.8rem] font-extrabold text-[#2855a6]"
                        }, {
                          default: _withCtx(() => [
                            _createTextVNode(" Last updated: " + _toDisplayString(formattedLastUpdated.value), 1)
                          ]),
                          _: 1
                        })
                      ])
                    ])
                  ]))
                : _createCommentVNode("", true)
            ])
          ]),
          _: 1
        }),
        _createVNode(AnimatedBox, {
          orientation: "y",
          delay: 0.08
        }, {
          default: _withCtx(() => [
            (showClientSelector.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                  _createElementVNode("div", _hoisted_8, [
                    _createElementVNode("div", _hoisted_9, [
                      _cache[3] || (_cache[3] = _createElementVNode("span", { class: "text-[0.75rem] font-extrabold uppercase tracking-[0.08em] text-[#748197]" }, " Client ", -1)),
                      _createVNode(_unref(Select), {
                        "model-value": __props.filters.client_id,
                        "onUpdate:modelValue": onClientUpdate
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_unref(SelectTrigger), { class: "min-h-12 rounded-[0.95rem] border-[rgba(211,220,233,1)] bg-[#fdfefe] px-[0.9rem] py-[0.82rem] text-[0.95rem] text-[#243049] shadow-[inset_0_1px_0_rgba(255,255,255,0.85)] focus:ring-[#8eb4ff] focus:ring-offset-0" }, {
                            default: _withCtx(() => [
                              _createVNode(_unref(SelectValue), { placeholder: "All Clients" })
                            ]),
                            _: 1
                          }),
                          _createVNode(_unref(SelectContent), { class: "rounded-[0.95rem] border-[rgba(211,220,233,1)] bg-white text-[#243049] shadow-[0_20px_45px_rgba(15,23,42,0.12)]" }, {
                            default: _withCtx(() => [
                              _createVNode(_unref(SelectItem), { value: "all" }, {
                                default: _withCtx(() => [...(_cache[2] || (_cache[2] = [
                                  _createTextVNode(" All Clients ", -1)
                                ]))]),
                                _: 1
                              }),
                              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.clients, (client) => {
                                return (_openBlock(), _createBlock(_unref(SelectItem), {
                                  key: client.id,
                                  value: client.client_id
                                }, {
                                  default: _withCtx(() => [
                                    _createTextVNode(_toDisplayString(client.company_name), 1)
                                  ]),
                                  _: 2
                                }, 1032, ["value"]))
                              }), 128))
                            ]),
                            _: 1
                          })
                        ]),
                        _: 1
                      }, 8, ["model-value"])
                    ]),
                    _createElementVNode("div", _hoisted_10, [
                      _cache[5] || (_cache[5] = _createElementVNode("span", { class: "text-[0.75rem] font-extrabold uppercase tracking-[0.08em] text-[#748197]" }, " Service ", -1)),
                      _createVNode(_unref(Select), {
                        "model-value": 
                __props.filters.service_id === null ? '' : String(__props.filters.service_id)
              ,
                        "onUpdate:modelValue": onServiceUpdate
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_unref(SelectTrigger), { class: "min-h-12 rounded-[0.95rem] border-[rgba(211,220,233,1)] bg-[#fdfefe] px-[0.9rem] py-[0.82rem] text-[0.95rem] text-[#243049] shadow-[inset_0_1px_0_rgba(255,255,255,0.85)] focus:ring-[#8eb4ff] focus:ring-offset-0" }, {
                            default: _withCtx(() => [
                              _createVNode(_unref(SelectValue), { placeholder: "All Services" })
                            ]),
                            _: 1
                          }),
                          _createVNode(_unref(SelectContent), { class: "rounded-[0.95rem] border-[rgba(211,220,233,1)] bg-white text-[#243049] shadow-[0_20px_45px_rgba(15,23,42,0.12)]" }, {
                            default: _withCtx(() => [
                              _createVNode(_unref(SelectItem), { value: "__all_services" }, {
                                default: _withCtx(() => [...(_cache[4] || (_cache[4] = [
                                  _createTextVNode(" All Services ", -1)
                                ]))]),
                                _: 1
                              }),
                              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.serviceOptions, (service) => {
                                return (_openBlock(), _createBlock(_unref(SelectItem), {
                                  key: service.value,
                                  value: service.value
                                }, {
                                  default: _withCtx(() => [
                                    _createTextVNode(_toDisplayString(service.label), 1)
                                  ]),
                                  _: 2
                                }, 1032, ["value"]))
                              }), 128))
                            ]),
                            _: 1
                          })
                        ]),
                        _: 1
                      }, 8, ["model-value"])
                    ]),
                    (showPackageSelector.value)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
                          _cache[7] || (_cache[7] = _createElementVNode("span", { class: "text-[0.75rem] font-extrabold uppercase tracking-[0.08em] text-[#748197]" }, " Package ", -1)),
                          _createVNode(_unref(Select), {
                            "model-value": 
                __props.filters.package_id === null ? '' : String(__props.filters.package_id)
              ,
                            "onUpdate:modelValue": onPackageUpdate
                          }, {
                            default: _withCtx(() => [
                              _createVNode(_unref(SelectTrigger), { class: "min-h-12 rounded-[0.95rem] border-[rgba(211,220,233,1)] bg-[#fdfefe] px-[0.9rem] py-[0.82rem] text-[0.95rem] text-[#243049] shadow-[inset_0_1px_0_rgba(255,255,255,0.85)] focus:ring-[#8eb4ff] focus:ring-offset-0" }, {
                                default: _withCtx(() => [
                                  _createVNode(_unref(SelectValue), { placeholder: "All Packages" })
                                ]),
                                _: 1
                              }),
                              _createVNode(_unref(SelectContent), { class: "rounded-[0.95rem] border-[rgba(211,220,233,1)] bg-white text-[#243049] shadow-[0_20px_45px_rgba(15,23,42,0.12)]" }, {
                                default: _withCtx(() => [
                                  _createVNode(_unref(SelectItem), { value: "__all_packages" }, {
                                    default: _withCtx(() => [...(_cache[6] || (_cache[6] = [
                                      _createTextVNode(" All Packages ", -1)
                                    ]))]),
                                    _: 1
                                  }),
                                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.packageOptions, (pkg) => {
                                    return (_openBlock(), _createBlock(_unref(SelectItem), {
                                      key: pkg.value,
                                      value: pkg.value
                                    }, {
                                      default: _withCtx(() => [
                                        _createTextVNode(_toDisplayString(pkg.label), 1)
                                      ]),
                                      _: 2
                                    }, 1032, ["value"]))
                                  }), 128))
                                ]),
                                _: 1
                              })
                            ]),
                            _: 1
                          }, 8, ["model-value"])
                        ]))
                      : _createCommentVNode("", true),
                    _createElementVNode("div", _hoisted_12, [
                      _cache[8] || (_cache[8] = _createElementVNode("span", { class: "text-[0.75rem] font-extrabold uppercase tracking-[0.08em] text-[#748197]" }, " Date From ", -1)),
                      _createVNode(_unref(Input), {
                        "model-value": __props.filters.date_from ?? '',
                        type: "date",
                        min: __props.dateFromMin || undefined,
                        class: "min-h-12 rounded-[0.95rem] border-[rgba(211,220,233,1)] bg-[#fdfefe] px-[0.9rem] py-[0.82rem] text-[0.95rem] text-[#243049] placeholder:text-[#8a95a9] focus-visible:ring-[#8eb4ff] focus-visible:ring-offset-0 focus-visible:shadow-[0_0_0_4px_rgba(99,149,255,0.12)]",
                        "onUpdate:modelValue": onDateFromUpdate
                      }, null, 8, ["model-value", "min"])
                    ]),
                    _createElementVNode("div", _hoisted_13, [
                      _cache[9] || (_cache[9] = _createElementVNode("span", { class: "text-[0.75rem] font-extrabold uppercase tracking-[0.08em] text-[#748197]" }, " Date To ", -1)),
                      _createVNode(_unref(Input), {
                        "model-value": __props.filters.date_to ?? '',
                        type: "date",
                        min: __props.dateFromMin || undefined,
                        class: "min-h-12 rounded-[0.95rem] border-[rgba(211,220,233,1)] bg-[#fdfefe] px-[0.9rem] py-[0.82rem] text-[0.95rem] text-[#243049] placeholder:text-[#8a95a9] focus-visible:ring-[#8eb4ff] focus-visible:ring-offset-0 focus-visible:shadow-[0_0_0_4px_rgba(99,149,255,0.12)]",
                        "onUpdate:modelValue": onDateToUpdate
                      }, null, 8, ["model-value", "min"])
                    ])
                  ])
                ]))
              : _createCommentVNode("", true)
          ]),
          _: 1
        }),
        _createVNode(_unref(Dialog), {
          open: specificAccountDialogOpen.value,
          "onUpdate:open": _cache[1] || (_cache[1] = ($event: any) => ((specificAccountDialogOpen).value = $event))
        }, {
          default: _withCtx(() => [
            _createVNode(_unref(DialogContent), { class: "max-h-[90vh] w-[min(92vw,44rem)] overflow-hidden rounded-[1.4rem] border border-[rgba(215,226,239,0.95)] bg-white p-0 shadow-[0_28px_80px_rgba(15,23,42,0.22)]" }, {
              default: _withCtx(() => [
                _createElementVNode("div", _hoisted_14, [
                  _createVNode(_unref(DialogHeader), { class: "gap-y-2 text-left" }, {
                    default: _withCtx(() => [
                      _createVNode(_unref(DialogTitle), { class: "text-[1.3rem] font-extrabold text-[#182033]" }, {
                        default: _withCtx(() => [...(_cache[10] || (_cache[10] = [
                          _createTextVNode(" Select Account Report ", -1)
                        ]))]),
                        _: 1
                      }),
                      _createVNode(_unref(DialogDescription), { class: "text-[#6e7b92]" }, {
                        default: _withCtx(() => [...(_cache[11] || (_cache[11] = [
                          _createTextVNode(" Choose a client account to generate a dashboard report using that account's dashboard data. ", -1)
                        ]))]),
                        _: 1
                      })
                    ]),
                    _: 1
                  })
                ]),
                _createElementVNode("div", _hoisted_15, [
                  _createElementVNode("div", _hoisted_16, [
                    _createVNode(_unref(Search), { class: "pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-[#94a3b8]" }),
                    _createVNode(_unref(Input), {
                      modelValue: specificAccountSearch.value,
                      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((specificAccountSearch).value = $event)),
                      class: "h-11 rounded-[0.95rem] border-[rgba(211,220,233,1)] bg-white pl-10 text-[0.95rem] text-[#243049] focus-visible:ring-[#8eb4ff] focus-visible:ring-offset-0",
                      placeholder: "Search accounts",
                      type: "text"
                    }, null, 8, ["modelValue"])
                  ]),
                  _createElementVNode("div", _hoisted_17, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filteredSpecificClients.value, (client) => {
                      return (_openBlock(), _createElementBlock("button", {
                        key: client.client_id,
                        type: "button",
                        class: _normalizeClass(["flex w-full items-start justify-between gap-3 rounded-[1rem] border px-4 py-3 text-left transition-colors", 
                selectedSpecificClientId.value === client.client_id
                  ? 'border-[#8eb4ff] bg-[#f4f8ff]'
                  : 'border-[rgba(216,228,240,0.95)] bg-white hover:bg-[#f8fbff]'
              ]),
                        onClick: ($event: any) => (selectedSpecificClientId.value = client.client_id)
                      }, [
                        _createElementVNode("div", _hoisted_19, [
                          _createElementVNode("p", _hoisted_20, _toDisplayString(client.company_name), 1),
                          _createElementVNode("p", _hoisted_21, " Client ID " + _toDisplayString(client.client_id), 1),
                          (client.package_name)
                            ? (_openBlock(), _createElementBlock("p", _hoisted_22, [
                                _cache[12] || (_cache[12] = _createTextVNode(" Package: ", -1)),
                                _createElementVNode("span", _hoisted_23, _toDisplayString(client.package_name), 1)
                              ]))
                            : _createCommentVNode("", true)
                        ]),
                        _createVNode(_unref(Badge), {
                          variant: "outline",
                          class: "rounded-full border-[rgba(142,180,255,0.35)] bg-[#f6f9ff] px-2.5 py-[0.2rem] text-[0.68rem] font-extrabold uppercase tracking-[0.08em] text-[#2855a6]"
                        }, {
                          default: _withCtx(() => [
                            _createTextVNode(_toDisplayString(client.active_services_count) + " Services ", 1)
                          ]),
                          _: 2
                        }, 1024)
                      ], 10, _hoisted_18))
                    }), 128)),
                    (!filteredSpecificClients.value.length)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_24, _toDisplayString(specificAccountSearch.value.trim()
                  ? "No matching accounts found."
                  : "No available accounts found for report generation."), 1))
                      : _createCommentVNode("", true)
                  ])
                ]),
                _createVNode(_unref(DialogFooter), { class: "border-t border-[rgba(227,233,243,0.95)] px-6 py-4" }, {
                  default: _withCtx(() => [
                    _createVNode(_unref(Button), {
                      variant: "outline",
                      type: "button",
                      class: "rounded-[0.95rem] border-[rgba(49,125,255,0.22)] bg-[#f6f9ff] px-4 py-2 font-bold text-[#2855a6] hover:bg-[#eef4ff]",
                      disabled: isReportActionBusy.value,
                      onClick: closeSpecificAccountDialog
                    }, {
                      default: _withCtx(() => [...(_cache[13] || (_cache[13] = [
                        _createTextVNode(" Cancel ", -1)
                      ]))]),
                      _: 1
                    }, 8, ["disabled"]),
                    _createVNode(_unref(Button), {
                      type: "button",
                      class: "rounded-[0.95rem] bg-[#1f7aff] px-4 py-2 font-bold text-white hover:bg-[#1f7aff]",
                      disabled: !selectedSpecificClientId.value || isReportActionBusy.value,
                      onClick: confirmSpecificAccountReport
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(isReportActionBusy.value ? "Preparing report..." : "Generate Report"), 1)
                      ]),
                      _: 1
                    }, 8, ["disabled"])
                  ]),
                  _: 1
                })
              ]),
              _: 1
            })
          ]),
          _: 1
        }, 8, ["open"])
      ]))
    : _createCommentVNode("", true)
}
}

})